<div class="fullcontainer pr-3">
  <div class='sheet'>
    <div class='titleContainer'>
      <h1 class='header'>Report - Batch Traceability</h1>
      <div>
        <!-- No "Batch Numbers" button. There is no batch list to browse: a batch exists only because a
             movement carries its number, so THIS report is where batches are read back from. -->
        <button class='btn btn-secondary' routerLink='/warehousing/reports'>All Reports</button>
      </div>
    </div>

    <div class='row'>
      <div class='col-md-3'>
        <label>Warehouse</label>
        <select class='form-control' (change)='this.onWarehouseChanged($event.target.value)'>
          <option value='-1'>All warehouses</option>
          @for (item of this.warehouses; track item.id) {
            <option [value]='item.id'>{{ item.name }}</option>
          }
        </select>
      </div>
      <div class='col-md-3'>
        <label>Location</label>
        <!-- Locations are scoped by warehouseId and the same aisle/shelf can exist at two sites, so
             this stays disabled until a warehouse is chosen. -->
        <select class='form-control'
          [disabled]='this.searchFilters.warehouseId === -1'
          [(ngModel)]='this.searchFilters.warehouseLocationId'>
          <option [value]='-1'>All locations</option>
          @for (item of this.locations; track item.id) {
            <option [value]='item.id'>{{ item.aisle }} / {{ item.shelf }}</option>
          }
        </select>
      </div>
      <div class='col-md-3'>
        <label>Product</label>
        <select class='form-control' [(ngModel)]='this.searchFilters.productId'>
          <option [value]='-1'>All products</option>
          @for (item of this.products; track item.id) {
            <option [value]='item.id'>{{ item.name }}</option>
          }
        </select>
      </div>
      <div class='col-md-3'>
        <label>Batch Number</label>
        <input type='text' name='batchNumber' maxlength='255' class='form-control'
          [(ngModel)]='this.searchFilters.batchNumber' />
        <small class='text-muted'>Matched exactly. Blank for every batch.</small>
      </div>
    </div>
    <br />
    <br />
    <button class='btn btn-primary' (click)='this.loadReport()' [disabled]='this.running'>Run Report</button>
    <hr />
    <button class='btn btn-secondary' (click)='exportToCSV()' [disabled]='!this.hasResults()'>Export to CSV</button>
  </div>

  <div class='sheet'>
    @if (this.running) {
      <p class='m-0'><i class="fas fa-spinner fa-spin"></i> Running&hellip;</p>
    }

    @if (this.hasRun && !this.running && !this.hasResults()) {
      <p class='m-0 text-muted'>No batch numbers and no stock movements matched those filters.</p>
    }

    @if (this.hasRun && this.hasResults()) {
      <div class='titleContainer'>
        <h1 class='header'>Results</h1>
      </div>

      <!-- Read off the payload's own `enforced` flag rather than written as a fixed sentence, so this
           screen cannot go on asserting non-enforcement after the backend has stopped meaning it. -->
      @if (!this.result.enforced) {
        <div class='alert alert-info'>
          <strong>Batch tracking does not enforce anything.</strong>
          A batch number is recorded on a stock movement and is consulted by no check. It does not
          reserve stock, does not scope a stock level and is not looked at when goods are booked out —
          so <strong>you can ship a batch you no longer hold</strong>, and a batch balance below can be
          negative. These figures are for tracing what happened, not for deciding what may happen.
        </div>
      } @else {
        <div class='alert alert-danger'>
          <strong>The system now reports batch balances as enforced.</strong>
          This screen was written when they were not, and its wording below has not been reviewed
          against that change. Do not rely on either until it has been.
        </div>
      }

      <!-- Called out above the tables rather than left as a flag in a column of numbers. On a batch
           this means something different from a negative location, and the difference is the action. -->
      @if (this.result.negativeBatches > 0) {
        <div class='alert alert-danger'>
          <strong>{{ this.result.negativeBatches }} batch balance(s) are negative.</strong>
          More was booked out against those batches than was ever booked in against them. The
          location is not short — the batch is: this is a mis-scan or a movement posted against the
          wrong number, not missing goods. The stock on the shelf is unaffected and the
          <button class='btn btn-sm btn-outline-dark ml-1' routerLink='/warehousing/reports/stock-on-hand'>
            stock on hand report
          </button>
          is where the real quantity is.
        </div>
      }

      <h3>What each location holds</h3>
      <p class='text-muted'>
        The whole balance at a product and location, and how much of it carries a batch number. Both
        parts and the total come from the same replay of the ledger, so the total is the balance
        itself rather than the two columns added together — three separate roundings of a float sum do
        not agree to the last digit, and the tolerance each was judged against is shown beside them.
      </p>

      @if (this.hasUnbatchedStock()) {
        <div class='alert alert-warning'>
          <strong>Batch balances do not add up to a location's stock, and that is arithmetic rather
          than a fault.</strong>
          Some stock at the locations below was booked in without a batch number, which is normal and
          is how most stock is handled. The un-batched remainder is the column named below and is a
          real quantity of real stock — not stock unaccounted for. Batched plus un-batched is the
          location's whole balance.
        </div>
      }

      <table class='table'>
        <thead>
          <th>Product</th>
          <th>SKU</th>
          <th>Warehouse</th>
          <th>Location</th>
          <th class='text-right'>Whole Balance</th>
          <th class='text-right'>Batched</th>
          <!-- Its own column, always drawn, including when it is zero: an omitted figure and a hidden
               zero look identical to a reader. -->
          <th class='text-right'>Un-batched Remainder</th>
          <th class='text-right'>Batches</th>
          <th class='text-right'>Tolerance</th>
        </thead>
        <tbody>
          @for (row of this.result.locations; track this.locationKey(row)) {
            <tr>
              <td>{{ row.productName }}</td>
              <td>{{ row.sku }}</td>
              <td>{{ row.warehouseName }}</td>
              <td>{{ row.locationLabel }}</td>
              <td class='text-right'>{{ this.formatQty(row.ledgerQty) }}</td>
              <td class='text-right'>{{ this.formatQty(row.batchedQty) }}</td>
              <td class='text-right'>{{ this.formatQty(row.unbatchedQty) }}</td>
              <td class='text-right'>{{ row.batches }}</td>
              <td class='text-right'><small class='text-muted'>{{ row.tolerance }}</small></td>
            </tr>
          }
        </tbody>
      </table>

      <h3 class='mt-4'>Batch balances</h3>
      <p class='text-muted'>
        One row per batch per location, replayed from that batch's own movements. A batch appears at
        every location it has ever moved through, including one it has left — its balance there is
        zero, and the row is the trace.
      </p>

      @if (this.result.rows.length === 0) {
        <p class='text-muted'>No movement carrying a batch number matched those filters.</p>
      } @else {
        <table class='table'>
          <thead>
            <th>Batch</th>
            <th>Product</th>
            <th>SKU</th>
            <th>Warehouse</th>
            <th>Location</th>
            <th class='text-right'>Balance</th>
            <th class='text-right'>Movements</th>
            <th class='text-right'>Net Weight</th>
            <th>Last Movement</th>
            <th></th>
          </thead>
          <tbody>
            @for (row of this.result.rows; track this.rowKey(row)) {
              <tr [class.table-danger]='this.isNegative(row)'>
                <td>{{ this.batchLabel(row) }}</td>
                <td>{{ row.productName }}</td>
                <td>{{ row.sku }}</td>
                <td>{{ row.warehouseName }}</td>
                <td>{{ row.locationLabel }}</td>
                <td class='text-right'>
                  {{ this.formatQty(row.qty) }}
                  @if (this.isNegative(row)) {
                    <span class='badge badge-danger ml-1'>negative</span>
                  }
                </td>
                <td class='text-right'>{{ row.movements }}</td>
                <!-- Blank where nothing in this batch was weighed at both ends. Not coloured on a
                     negative net: a batch going out lighter than it came in is the expected
                     reading, same reasoning as the adjustments and movement-history tables. -->
                <td class='text-right'>
                  {{ this.formatWeight(row.netWeight) }}
                  @if (this.hasUnweighed(row)) {
                    <br />
                    <small class='text-muted'>{{ row.weighedMovements }} of {{ row.movements }} weighed</small>
                  }
                </td>
                <td>{{ this.formatDateTime(row.lastMovementAt) }}</td>
                <td>
                  <button class='btn btn-warning'
                    routerLink='/warehousing/reports/movement-history'
                    [queryParams]='{productId: row.productId}'>Audit trail</button>
                </td>
              </tr>
            }
          </tbody>
        </table>
      }

      <h3 class='mt-4'>Un-batched remainder</h3>
      <p class='text-muted'>
        Stock at these locations that was moved without any batch number. Drawn as its own section
        rather than folded into the table above, because it is not a batch and must never be read as
        one. Shown whenever the filters match a location, including where the remainder is zero —
        a zero here is the statement that everything at that location is numbered.
        @if (this.searchFilters.batchNumber) {
          It is <strong>not</strong> narrowed by the batch number you searched for: a remainder is
          what carries no number, so naming one cannot select or deselect it.
        }
      </p>

      <!-- Empty is legitimate and is NOT the same as zero un-batched stock. The authoritative
           remainder is the `Un-batched` column on the locations table above, which is reported
           whether it is zero or not; this section is the per-row detail behind it. -->
      @if (this.result.unbatchedDetail.length === 0) {
        <p class='text-muted'>Every movement matching those filters carried a batch number.</p>
      } @else {
        <table class='table'>
          <thead>
            <th>Product</th>
            <th>SKU</th>
            <th>Warehouse</th>
            <th>Location</th>
            <th class='text-right'>Balance</th>
            <th class='text-right'>Movements</th>
            <!-- A revaluation is money moving without stock moving. It can only ever land here: the
                 posting path refuses a batch on one outright. -->
            <th class='text-right'>Revaluations</th>
            <th class='text-right'>Net Weight</th>
            <th>Last Movement</th>
          </thead>
          <tbody>
            @for (row of this.result.unbatchedDetail; track this.rowKey(row)) {
              <tr [class.table-danger]='this.isNegative(row)'>
                <td>{{ row.productName }}</td>
                <td>{{ row.sku }}</td>
                <td>{{ row.warehouseName }}</td>
                <td>{{ row.locationLabel }}</td>
                <td class='text-right'>{{ this.formatQty(row.qty) }}</td>
                <td class='text-right'>{{ row.movements }}</td>
                <td class='text-right'>{{ row.revaluations }}</td>
                <td class='text-right'>
                  {{ this.formatWeight(row.netWeight) }}
                  @if (this.hasUnweighed(row)) {
                    <br />
                    <small class='text-muted'>{{ row.weighedMovements }} of {{ row.movements }} weighed</small>
                  }
                </td>
                <td>{{ this.formatDateTime(row.lastMovementAt) }}</td>
              </tr>
            }
          </tbody>
        </table>
      }

      <!-- There is deliberately no "batches nothing has moved" section. With no batch table a batch
           cannot exist without a movement - being typed onto one is the only way a number comes into
           existence - so the set is empty by construction rather than merely unreported, and a screen
           carrying the heading would imply a registry that could hold an unmoved batch. -->
    }
  </div>
</div>
